import { createAuth0Client, Auth0Client, GetTokenSilentlyVerboseResponse, User } from '@auth0/auth0-spa-js';
import { AUTH0_CLIENT_ID, AUTH0_DOMAIN, AUTH0_AUDIENCE } from '../constants/config';

// Auth0 client for the Universal Login redirect flow.
//
// Never export a `getToken` from here: the sync getToken() in actionCreators.ts
// is called from 28 non-async thunks, and an async twin of that name is how a
// Promise ends up in an Authorization header.

let clientPromise: Promise<Auth0Client> | null = null;

/**
 * The Auth0 client, created once and reused. Caches the promise rather than the
 * client so concurrent bootstrap callers share one creation; cleared on failure
 * so a retry is possible.
 */
export function getAuth0Client(): Promise<Auth0Client> {
  if (clientPromise) {
    return clientPromise;
  }

  // All three are `string | undefined` (process.env via DefinePlugin).
  if (!AUTH0_CLIENT_ID || !AUTH0_DOMAIN || !AUTH0_AUDIENCE) {
    return Promise.reject(new Error(
      'Auth0 is not configured: AUTH0_CLIENT_ID, AUTH0_DOMAIN and AUTH0_AUDIENCE '
      + 'must all be set. Run `. set_env.txt` before starting the dev server, and '
      + 'make sure AUTH0_CLIENT_ID is set in the Heroku config for deployments.'
    ));
  }

  clientPromise = createAuth0Client({
    domain: AUTH0_DOMAIN,
    clientId: AUTH0_CLIENT_ID,
    // Default is memory-only, so a reload would lose the cache.
    cacheLocation: 'localstorage',
    // Refresh tokens rather than the hidden-iframe technique, which needs the
    // Auth0 domain same-site with the app (taplause.com vs herokuapp.com: not).
    // Needs Allow Offline Access + Refresh Token Rotation on the Auth0 app. The
    // production client cannot have rotation until it is made oidc_conformant on
    // release day, so develop against a separate client.
    //
    // Without them getTokenSilently() throws `missing_refresh_token` (no iframe
    // fallback in v2) — but only once the cached token expires, so "login
    // worked" does not prove it is configured. The SDK adds `offline_access`.
    useRefreshTokens: true,
    authorizationParams: {
      audience: AUTH0_AUDIENCE,
      scope: 'openid profile email',
      redirect_uri: window.location.origin,
    },
  }).catch((error: unknown) => {
    clientPromise = null;
    throw error;
  });

  return clientPromise;
}

/** Exchanges the `?code=&state=` Auth0 sends back. Must run before the router mounts. */
export async function handleRedirectCallback(): Promise<void> {
  const client = await getAuth0Client();
  await client.handleRedirectCallback();
}

/** Access token plus `expires_in`, which the bootstrap needs to compute `expiresAt`. */
export async function getAccessToken(): Promise<GetTokenSilentlyVerboseResponse> {
  const client = await getAuth0Client();
  return client.getTokenSilently({ detailedResponse: true });
}

/**
 * Forces a new access token, bypassing the cache — this is what actually spends
 * the refresh token and rotates it. `getAccessToken()` would return the cached
 * token instead, which the refresher would then re-stamp with a fresh deadline
 * without ever replacing it.
 */
export async function refreshAccessToken(): Promise<GetTokenSilentlyVerboseResponse> {
  const client = await getAuth0Client();
  return client.getTokenSilently({ detailedResponse: true, cacheMode: 'off' });
}

/** The authenticated user's profile, or undefined when there is no session. */
export async function getUser(): Promise<User | undefined> {
  const client = await getAuth0Client();
  return client.getUser<User>();
}

/** Whether the SDK currently holds a valid session. */
export async function isAuthenticated(): Promise<boolean> {
  const client = await getAuth0Client();
  return client.isAuthenticated();
}
